import { realtimeConfig } from '@/realtime/realtime-config'

/**
 * Build identity strip, pinned to the bottom of the sidebar.
 *
 * Mirrors the activity status bar's height and border on the other side of the
 * shell, so the two read as one continuous band across the bottom of the app
 * rather than as a bar with a ragged edge beside it.
 *
 * It answers "which build am I looking at?", which matters most when several
 * stages are reachable from the same browser and look identical. The stage comes
 * from the realtime topic prefix — the only place the deployment's own stage name
 * currently reaches the client — and is omitted rather than guessed when the
 * channel is unconfigured.
 */

/** Kept in step with the status bar's height so the bottom edge is one band. */
const BAR_HEIGHT_CLASS = 'h-7'

/**
 * Deployment stage, taken from the `{app}/{stage}` realtime prefix.
 *
 * @returns {string | null} The stage name, or null when realtime is unconfigured.
 */
function currentStage(): string | null {
	const prefix = realtimeConfig?.topicPrefix
	if (!prefix) return null
	const stage = prefix.slice(prefix.indexOf('/') + 1)
	return stage.length > 0 ? stage : null
}

export function BuildInfoBar() {
	const stage = currentStage()

	return (
		// Justified rather than packed left, mirroring the status bar across the
		// shell: identity on the outer edge, environment on the inner one, so the two
		// bars read as a single band with content at both ends.
		// Laid out at the sidebar's expanded width whatever the rail's state, like the
		// nav: the collapsed rail clips it rather than squeezing it, so the version is
		// never cut short. The version keeps its full width; a long stage name gives
		// way, and its title carries it in full.
		<div
			className={`flex ${BAR_HEIGHT_CLASS} w-49 shrink-0 items-center justify-between gap-ds-2 border-t border-ds-border px-ds-3 text-ds-xs text-ds-text-subtle`}
		>
			<span className="shrink-0 whitespace-nowrap">
				<span className="sr-only">Application version </span>v{__APP_VERSION__}
			</span>
			{stage ? (
				<span className="flex min-w-0 items-center gap-ds-1">
					<span aria-hidden className="uppercase">
						env
					</span>
					<span className="sr-only">Environment</span>
					<span title={stage} className="truncate font-mono text-ds-text-muted">
						{stage}
					</span>
				</span>
			) : null}
		</div>
	)
}
